package fr.frux.navigation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.layout.FruxLayoutDefaults
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode

/**
 * Colonne grand écran posée sur la carte (spec 2026-09-10 §4 et §5), habillage de l'écran empilé
 * lui-même et non de l'hôte de la pile : c'est l'entrée Nav3 qui porte sa colonne, donc les
 * animations du NavDisplay déplacent la colonne avec son écran, et l'écran Explorer qui la
 * précède garde toute sa largeur pendant la transition (un hôte qui changeait de largeur
 * écrasait l'écran sortant dans 400 dp puis le faisait sauter à pleine zone).
 *
 * Porte aussi la colonne de 640 dp des écrans de pleine zone qui ne sont pas marqués larges
 * ([wideFullZoneMetadata]), pour la même raison : l'hôte garde sa largeur d'un écran à l'autre.
 *
 * Un seul site de composition : hors grand écran ou hors colonne, le même Box occupe tout
 * l'hôte, seuls les modificateurs changent. Un redimensionnement ne perd donc aucun état.
 */
@Composable
fun MapPanel(
    zone: LayoutZone?,
    wide: Boolean = false,
    content: @Composable () -> Unit,
) {
    val expanded = LocalFruxLayoutMode.current == FruxLayoutMode.Expanded
    val surface = MaterialTheme.colorScheme.surface
    Box(
        modifier =
            mapPanelModifier(
                panel = expanded && zone == LayoutZone.MapZone,
                fullZoneColumn = expanded && zone == LayoutZone.FullZone && !wide,
                fullZoneWide = expanded && zone == LayoutZone.FullZone && wide,
                surface = surface,
            ),
    ) {
        content()
    }
}

/**
 * Fonction pure de modificateurs, hors composition : changer de modificateur remesure le nœud,
 * changer de composable le détruirait.
 */
private fun mapPanelModifier(
    panel: Boolean,
    fullZoneColumn: Boolean,
    fullZoneWide: Boolean,
    surface: Color,
): Modifier =
    if (fullZoneWide) {
        // Écran large de pleine zone : contre le rail comme une colonne sur la carte, borné pour
        // que les lignes de liste ne s'étirent pas sur tout un grand écran.
        Modifier
            .fillMaxSize()
            .wrapContentWidth(Alignment.Start)
            .widthIn(max = FruxLayoutDefaults.FullZoneWideMaxWidth)
            .fillMaxWidth()
            .fillMaxHeight()
    } else if (fullZoneColumn) {
        // Colonne de pleine zone contre le rail, comme le Profil dont ces écrans sont les
        // sous-pages : ouvrir Réglages ne déplace pas le contenu d'un bord à l'autre (#443).
        // Même principe que la colonne sur la carte, l'écran porte sa largeur, l'hôte ne bouge pas.
        Modifier
            .fillMaxSize()
            .wrapContentWidth(Alignment.Start)
            .widthIn(max = FruxLayoutDefaults.FullZoneMaxWidth)
            .fillMaxWidth()
            .fillMaxHeight()
    } else if (panel) {
        Modifier
            .width(FruxLayoutDefaults.MapPanelWidth)
            .fillMaxHeight()
            // Ombre AVANT le fond : l'ordre des modificateurs décide de ce qui est dessiné.
            .shadow(elevation = MapPanelElevation)
            .background(surface)
            // Sans ce pointerInput vide, un tap dans la colonne traverse jusqu'à la carte
            // composée dessous (un Box opaque n'intercepte pas les gestes par défaut).
            .pointerInput(Unit) {}
    } else {
        Modifier.fillMaxSize()
    }

/** Détachement de la colonne par rapport à la carte qu'elle recouvre. */
private val MapPanelElevation = 8.dp
